import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import { formatTimeForInput, parseLooseTime } from './parseTime';
import { Plan, Step, CustomStep, DrivingStep } from './types';
import { computePlan } from './compute';
import StepCard from './StepCard';
import LeaveByAppBar, { SaveState } from './LeaveByAppBar';
import Timeline from './Timeline';

const DEFAULT_FROM_ADDRESS = '358 Red Osier Road, Waterloo, ON';

const useStyles = makeStyles(theme => ({
  root: {
    textAlign: 'left',
  },
  body: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '0.6em 0.6em 1.5em',
    [theme.breakpoints.up('sm')]: {
      padding: '1em 1em 2em',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 1100,
      display: 'flex',
      gap: 24,
      alignItems: 'flex-start',
    },
  },
  mainCol: {
    [theme.breakpoints.up('md')]: {
      flex: '1 1 auto',
      minWidth: 0,
      maxWidth: 720,
    },
  },
  sidebar: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      flex: '0 0 320px',
      position: 'sticky',
      top: 96,
      maxHeight: 'calc(100vh - 110px)',
      overflowY: 'auto',
      padding: '0.5em 0',
    },
  },
  apptCard: {
    padding: '0.75em',
    marginBottom: '0.6em',
    [theme.breakpoints.up('sm')]: {
      padding: '1em',
    },
  },
  apptRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.6em',
    alignItems: 'center',
    marginTop: '0.5em',
  },
  apptField: {
    flex: '1 1 160px',
    minWidth: 0,
  },
  nameField: {
    width: '100%',
    marginBottom: '0.5em',
  },
  addRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '0.4em',
    margin: '0.5em 0 1em',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
  },
  addRowLabel: {
    textAlign: 'center',
    color: '#555',
    marginBottom: '0.1em',
    [theme.breakpoints.up('sm')]: {
      flexBasis: '100%',
    },
  },
  addBtnGroup: {
    display: 'flex',
    gap: '0.4em',
  },
  addBtn: {
    flex: 1,
  },
  keyboardBridge: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: 'none',
  },
  empty: {
    padding: '1.5em 1em',
    textAlign: 'center',
    color: '#666',
  },
}));

function makeId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function newCustomStep(name = 'New step', durationMinutes = 15): CustomStep {
  return {
    id: makeId(),
    type: 'custom',
    name,
    durationMinutes,
  };
}

function newDrivingStep(): DrivingStep {
  return {
    id: makeId(),
    type: 'driving',
    name: 'Drive',
    origin: DEFAULT_FROM_ADDRESS,
    destination: '',
    drivingMinutes: null,
    paddingValue: 10,
    paddingUnit: 'percent',
  };
}

interface Props {
  plan: Plan;
  isPersisted: boolean;
  onSave: (plan: Plan) => Promise<Plan>;
  plans: Plan[];
  plansLoading: boolean;
  onOpenPlan: (planId: string) => void;
  onCreatePlan: () => void;
  onDeletePlan: (planId: string) => void;
}

function suggestedPlanName(plan: Plan) {
  const appointment = plan.appointmentTime ? new Date(plan.appointmentTime) : null;
  if (appointment && !isNaN(appointment.getTime())) {
    return `Plan for ${appointment.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
  }
  return 'Untitled plan';
}

export default function PlanEditor({
  plan: initialPlan,
  isPersisted,
  onSave,
  plans,
  plansLoading,
  onOpenPlan,
  onCreatePlan,
  onDeletePlan,
}: Props) {
  const classes = useStyles();
  const [plan, setPlan] = useState<Plan>(initialPlan);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [focusStepId, setFocusStepId] = useState<string | null>(null);
  const lastSavedRef = useRef<string>(JSON.stringify(initialPlan));
  const keyboardBridgeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setPlan(initialPlan);
    lastSavedRef.current = JSON.stringify(initialPlan);
    setSaveState('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlan.id]);

  const computed = useMemo(() => computePlan(plan), [plan]);
  const isDirty = JSON.stringify(plan) !== lastSavedRef.current;

  const markDirty = useCallback((updater: (current: Plan) => Plan) => {
    setSaveState('idle');
    setPlan(updater);
  }, []);

  const updateField = useCallback(<K extends keyof Plan>(key: K, value: Plan[K]) => {
    markDirty(p => ({ ...p, [key]: value }));
  }, [markDirty]);

  const updateStep = useCallback((id: string, next: Step) => {
    markDirty(p => ({ ...p, steps: p.steps.map(s => s.id === id ? next : s) }));
  }, [markDirty]);

  const deleteStep = useCallback((id: string) => {
    markDirty(p => ({ ...p, steps: p.steps.filter(s => s.id !== id) }));
  }, [markDirty]);

  const moveStep = useCallback((id: string, direction: -1 | 1) => {
    markDirty(p => {
      const idx = p.steps.findIndex(s => s.id === id);
      if (idx < 0) return p;
      const target = idx + direction;
      if (target < 0 || target >= p.steps.length) return p;
      const next = p.steps.slice();
      const [removed] = next.splice(idx, 1);
      next.splice(target, 0, removed);
      return { ...p, steps: next };
    });
  }, [markDirty]);

  const addStepAtStart = useCallback((step: Step) => {
    setFocusStepId(step.id);
    markDirty(p => ({ ...p, steps: [step, ...p.steps] }));
  }, [markDirty]);

  const primeMobileKeyboard = () => {
    keyboardBridgeRef.current?.focus();
  };

  const apptDateValue = useMemo(() => {
    if (!plan.appointmentTime) return null;
    const d = new Date(plan.appointmentTime);
    return isNaN(d.getTime()) ? null : d;
  }, [plan.appointmentTime]);

  const [apptText, setApptText] = useState(() => formatTimeForInput(apptDateValue));

  useEffect(() => {
    setApptText(formatTimeForInput(apptDateValue));
  }, [apptDateValue]);

  const setApptFromTime = useCallback((hours: number, minutes: number) => {
    const base = plan.appointmentTime ? new Date(plan.appointmentTime) : new Date();
    if (isNaN(base.getTime())) base.setTime(Date.now());
    base.setHours(hours, minutes, 0, 0);
    updateField('appointmentTime', base.toISOString());
  }, [plan.appointmentTime, updateField]);

  const updateApptText = (value: string) => {
    setApptText(value);
    const trimmed = value.trim();
    if (!trimmed) {
      updateField('appointmentTime', '');
      return;
    }
    const parsed = parseLooseTime(trimmed);
    if (parsed) setApptFromTime(parsed.hours, parsed.minutes);
  };

  const handleSave = async () => {
    let nextPlan = plan;
    const shouldAskForName = !isPersisted || !plan.name.trim() || plan.name === 'New plan';
    if (shouldAskForName) {
      const name = window.prompt('Name this plan', plan.name === 'New plan' ? suggestedPlanName(plan) : plan.name);
      if (name === null) return;
      nextPlan = { ...plan, name: name.trim() || 'Untitled plan' };
      setPlan(nextPlan);
    }

    setSaveState('saving');
    try {
      const savedPlan = await onSave(nextPlan);
      setPlan(savedPlan);
      lastSavedRef.current = JSON.stringify(savedPlan);
      setSaveState('saved');
    } catch (err) {
      console.error('Save failed', err);
      setSaveState('error');
    }
  };

  const displayedName = isPersisted ? (plan.name || 'Untitled plan') : '';

  return (
    <div className={classes.root}>
      <LeaveByAppBar
        computed={computed}
        appointmentTime={plan.appointmentTime}
        planName={displayedName}
        isPersisted={isPersisted}
        isDirty={isDirty}
        saveState={saveState}
        onSave={handleSave}
        plans={plans}
        plansLoading={plansLoading}
        onOpenPlan={onOpenPlan}
        onCreatePlan={onCreatePlan}
        onDeletePlan={onDeletePlan}
      />

      <div className={classes.body}>
        <input
          ref={keyboardBridgeRef}
          className={classes.keyboardBridge}
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className={classes.mainCol}>
        <div className={classes.addRow}>
          <div className={classes.addBtnGroup}>
            <Button
              className={classes.addBtn}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onMouseDown={primeMobileKeyboard}
              onTouchStart={primeMobileKeyboard}
              onClick={() => addStepAtStart(newCustomStep())}
            >
              Step
            </Button>
            <Button
              className={classes.addBtn}
              variant="contained"
              color="primary"
              startIcon={<DirectionsCarIcon />}
              onMouseDown={primeMobileKeyboard}
              onTouchStart={primeMobileKeyboard}
              onClick={() => addStepAtStart(newDrivingStep())}
            >
              Drive
            </Button>
          </div>
        </div>

        {plan.steps.map((step, i) => (
          <StepCard
            key={step.id}
            step={step}
            index={i}
            total={plan.steps.length}
            focusName={step.id === focusStepId}
            onChange={updateStep}
            onDelete={deleteStep}
            onMove={moveStep}
          />
        ))}

        <Paper className={classes.apptCard}>
          <Typography variant="subtitle1">Appointment</Typography>
          <div className={classes.apptRow}>
            <TextField
              className={classes.apptField}
              label="Appointment time"
              type="time"
              variant="outlined"
              size="small"
              value={apptText}
              onChange={e => updateApptText(e.target.value)}
              helperText=" "
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              className={classes.apptField}
              type="number"
              label="Arrive early (min)"
              variant="outlined"
              size="small"
              value={plan.arriveEarlyMinutes ?? ''}
              onChange={e => updateField('arriveEarlyMinutes', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
              inputProps={{ min: 0, inputMode: 'numeric' }}
              helperText=" "
            />
          </div>
        </Paper>
        </div>

        <aside className={classes.sidebar}>
          <Timeline computed={computed} hasAppt={!!plan.appointmentTime && !!computed.appointmentAt} />
        </aside>
      </div>
    </div>
  );
}
